import { computed, Injectable, signal } from '@angular/core';

/**
 * Tracks whether any widget area is running a layout-edit session ("Indeling bewerken" on
 * `WidgetFlowAreaComponent`). Lives in shared/, like `DrawerInsetService` one file over and for
 * the same reason: the writer is the widget feature, but the reader is the app-wide toolbar —
 * `IconMenuItemsComponent` disables the quick-view drawer button while a session runs (user
 * decision 2026-08-25). The drawer and an edit session both claim the viewport's right edge;
 * `startEditing` already closes an open drawer on entry, and that toolbar button is the one way
 * to reopen it mid-session that the widget feature itself cannot see.
 *
 * A COUNTER rather than a boolean, mirroring `DrawerInsetService`: one flow area mounts per
 * dashboard today, but the component takes an area `id` input and nothing structural prevents two
 * areas on one route. With a boolean, whichever session ended first would re-enable the button
 * under the other one; with a counter that case is simply not expressible.
 */
@Injectable({ providedIn: 'root' })
export class WidgetLayoutEditService {
  readonly #activeSessions = signal(0);

  /** True while at least one layout-edit session runs. */
  readonly anyEditing = computed(() => this.#activeSessions() > 0);

  /** Claim a session. Paired with exactly one `release()` — `WidgetFlowAreaComponent` pairs them
   *  on the edit-mode edges and settles the balance on destroy. */
  retain(): void {
    this.#activeSessions.update(count => count + 1);
  }

  /** End a session. Floors at zero: an unbalanced release (a spec driving the discard path
   *  outside a session, say) must not push the counter negative and make a later `retain()` a
   *  no-op. */
  release(): void {
    this.#activeSessions.update(count => Math.max(0, count - 1));
  }
}
